import { useState } from 'react'

import { ActionIcon, Collapse, Loader, Menu, NumberInput } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'
import { isOps } from 'src/lib/auth/permissions'
import {
  CountryConfig,
  CountryConfigItem,
  findIssuingCountry,
} from 'src/lib/country.utils'
import { formatExperience } from 'src/lib/formatters'
import {
  formatInternationalPhoneNumber,
  removeCountryCode,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'
import { unformatSsn } from 'src/lib/ssn.utils'

import DateInput from '../../../../../components/Inputs/DateInput/DateInput'

import PasswordInput from './PasswordInput/PasswordInput'
import TextInput from './TextInput/TextInput'

const PersonalInformationCard = ({
  values: appValues = [],
  handleUpdateApplication,
  initialOpened = true,
}) => {
  const { currentUser } = useAuth()
  const isOpsUser = isOps(currentUser)

  const [values, setValues] = useState({ ...appValues[0] })
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [opened, setOpened] = useState(initialOpened)
  const existingIssuingCountry = values?.issuingCountry
    ? findIssuingCountry(values.issuingCountry)
    : undefined
  const [issuingCountryConfig, setIssuingCountry] = useState<CountryConfigItem>(
    existingIssuingCountry || CountryConfig.US
  )

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...appValues[0] })
    setEditing(false)
  }

  const handleSave = async () => {
    if (validatePhone(values.phone) !== undefined) return

    setLoading(true)
    try {
      const updates = {
        ...(values.dob !== undefined && { dob: values.dob }),
        // Only include SSN updates if user is not OPS
        ...(!isOpsUser &&
          values.ssn !== undefined && { ssn: unformatSsn(values.ssn) }),
        ...(!isOpsUser &&
          values.issuingCountry !== undefined && {
            issuingCountry: values.issuingCountry,
          }),
        ...(values.phone !== undefined && {
          phone: unformatPhoneNumber(values.phone),
        }),
        ...(values.firstName !== undefined && { firstName: values.firstName }),
        ...(values.middleName !== undefined && {
          middleName: values.middleName,
        }),
        ...(values.lastName !== undefined && { lastName: values.lastName }),
        ...(values.email !== undefined && { email: values.email }),
        ...(values.yearsOfExperience !== undefined && {
          yearsOfExperience: values.yearsOfExperience != null
            ? parseFloat(values.yearsOfExperience)
            : null,
        }),
      }

      const result = await handleUpdateApplication(updates)
      if (!result.errors) {
        setEditing(false)
      }
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false)
    }
  }

  const validatePhone = (phone) => {
    if (!phone) return undefined

    const phoneLength = unformatPhoneNumber(removeCountryCode(phone)).length
    if (phoneLength !== 10) {
      return 'Number must be 10 digits'
    }
    return undefined
  }

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="text-md flex w-full cursor-pointer flex-row items-center gap-2 font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Personal Information
        {!editing ? (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        ) : (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading || validatePhone(values.phone) !== undefined}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
            {loading && <Loader size={12} color="gray" />}
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          <div className={`grid grid-cols-4 gap-3`}>
        <TextInput
          label="First Name"
          editing={editing}
          value={values.firstName}
          handleChange={(value) => setValues({ ...values, firstName: value })}
        />
        <TextInput
          label="Middle Name"
          editing={editing}
          value={values.middleName}
          handleChange={(value) => setValues({ ...values, middleName: value })}
        />
        <TextInput
          label="Last Name"
          editing={editing}
          value={values.lastName}
          handleChange={(value) => setValues({ ...values, lastName: value })}
        />
        {editing ? (
          <NumberInput
            label="Experience"
            value={
              values.yearsOfExperience != null
                ? parseFloat(values.yearsOfExperience)
                : undefined
            }
            onChange={(value) =>
              setValues({ ...values, yearsOfExperience: value })
            }
            min={0}
            max={80}
            decimalScale={1}
            step={0.5}
            placeholder="Years"
            classNames={{
              label: 'text-sm font-medium text-doubleNickel-gray-700',
            }}
          />
        ) : (
          <StyledReadOnlyInput
            label="Experience"
            value={formatExperience(values.yearsOfExperience) || '-'}
          />
        )}
        {editing ? (
          <PhoneInput
            label="Phone"
            editing={editing}
            value={values.phone}
            onChange={(value) => setValues({ ...values, phone: value })}
            error={validatePhone(values.phone)}
          />
        ) : (
          <StyledReadOnlyInput
            label="Phone"
            value={formatInternationalPhoneNumber(values.phone)}
          />
        )}
        <TextInput
          label="Email"
          editing={editing}
          value={values.email}
          handleChange={(value) => setValues({ ...values, email: value })}
        />
        <DateInput
          label="Date of Birth"
          editing={editing}
          value={values.dob}
          onChange={(dob) => {
            setValues({ ...values, dob: dob })
          }}
        />
        {!isOpsUser && (
          <PasswordInput
            label="Social Security Number"
            editing={editing}
            value={values.ssn}
            handleChange={(value) => {
              setValues({
                ...values,
                ssn: value,
              })
            }}
            leftSectionWidth={editing ? '60px' : undefined}
            leftSectionPointerEvents={'all'}
            leftSection={
              editing ? (
                <Menu shadow="md">
                  <Menu.Target>
                    <div className="flex flex-row items-center gap-1 text-nowrap px-2 text-sm font-medium text-doubleNickel-gray-800 hover:cursor-pointer">
                      {issuingCountryConfig.code}
                      <IconChevronDown className="h-4 fill-none stroke-doubleNickel-gray-400" />
                    </div>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {Object.values(CountryConfig).map((config) => (
                      <Menu.Item
                        key={config.code}
                        onClick={() => setIssuingCountry(config)}
                      >
                        {config.code}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              ) : null
            }
          />
        )}
      </div>
    </div>
  </Collapse>
    </div>
  )
}

export default PersonalInformationCard
